import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Warning, X, Crown, Lightning, Clock } from "@phosphor-icons/react";

function formatHMS(ms) {
  if (ms <= 0) return "00:00:00";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function SubscriptionExpiryBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [now, setNow] = useState(Date.now());

  // Re-render every second so the countdown updates live
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!user || user.role === "admin" || dismissed) return null;

  const expiresAt = user.subscription_expires_at;
  if (!expiresAt) return null;

  const msLeft = new Date(expiresAt) - now;
  if (msLeft < 0) return null;

  // Only show banner if 7 days or fewer remain
  const daysLeft = msLeft / 86400000;
  if (daysLeft > 7) return null;

  const validStatus = user.subscription_status === "active" || user.subscription_status === "trialing";
  if (!validStatus) return null;

  const isInLast24h = msLeft < 86400000;          // < 24h → live countdown in red
  const isUrgent    = msLeft < 2 * 86400000;       // <48h → red theme
  const wholeDays   = Math.ceil(daysLeft);         // ceil so 23h59m shows as "1 día" not "0"

  const expiryNode = isInLast24h
    ? (
      <span className="flex items-center gap-1.5">
        <Clock weight="fill" className="w-3 h-3" style={{ color: "#F87171" }} />
        <span className="text-xs font-black uppercase tracking-wide" style={{ color: "#F87171" }}>Vence en</span>
        <span
          data-testid="expiry-countdown"
          className="font-mono font-black tabular-nums"
          style={{
            color: "#FF4444",
            textShadow: "0 0 8px rgba(255,68,68,0.5)",
            fontSize: "13px",
            letterSpacing: "0.02em",
          }}
        >
          {formatHMS(msLeft)}
        </span>
      </span>
    )
    : (
      <span
        className="text-xs font-black uppercase tracking-wide flex-shrink-0"
        style={{ color: isUrgent ? "#F87171" : "#D4AF37" }}
      >
        {wholeDays === 1 ? "Tu membresía vence MAÑANA" : `Tu membresía vence en ${wholeDays} días`}
      </span>
    );

  return (
    <div
      data-testid="subscription-expiry-banner"
      className="flex-shrink-0 w-full"
      style={{
        background: isUrgent
          ? "linear-gradient(135deg, rgba(239,68,68,0.12) 0%, rgba(239,68,68,0.05) 100%)"
          : "linear-gradient(135deg, rgba(212,175,55,0.1) 0%, rgba(212,175,55,0.04) 100%)",
        borderBottom: isUrgent
          ? "1px solid rgba(239,68,68,0.3)"
          : "1px solid rgba(212,175,55,0.25)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
            style={{
              background: isUrgent ? "rgba(239,68,68,0.15)" : "rgba(212,175,55,0.12)",
              border: isUrgent ? "1px solid rgba(239,68,68,0.35)" : "1px solid rgba(212,175,55,0.3)",
            }}
          >
            {isUrgent
              ? <Warning weight="fill" className="w-3.5 h-3.5 text-red-400" />
              : <Crown weight="fill" className="w-3.5 h-3.5" style={{ color: "#D4AF37" }} />
            }
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 min-w-0">
            {expiryNode}
            <span className="hidden sm:block text-zinc-500 text-xs">·</span>
            <span className="text-xs text-zinc-400 truncate">
              Renueva para no perder acceso a las jugadas premium
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to="/plans"
            data-testid="expiry-banner-renew-btn"
            className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-[11px] font-black uppercase tracking-wider transition-all hover:opacity-90"
            style={{
              background: isUrgent ? "rgba(239,68,68,0.2)" : "rgba(212,175,55,0.15)",
              color: isUrgent ? "#F87171" : "#D4AF37",
              border: isUrgent ? "1px solid rgba(239,68,68,0.4)" : "1px solid rgba(212,175,55,0.35)",
            }}
          >
            <Lightning weight="fill" className="w-3 h-3" />
            Renovar
          </Link>
          <button
            onClick={() => setDismissed(true)}
            data-testid="expiry-banner-dismiss"
            className="text-zinc-600 hover:text-zinc-400 transition-colors p-1"
            aria-label="Cerrar"
          >
            <X weight="bold" className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
